/**
 * Review Page
 *
 * Priority-based task review interface following the "mirror not policeman" philosophy.
 * Allows users to review, touch, pin, and manage task priorities.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { 
  Pin, 
  PinOff, 
  ChevronUp, 
  ChevronDown, 
  Check, 
  RefreshCw, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  History,
  Sparkles,
  Mountain,
  X,
  FileText,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useTasks, useTaskMutations } from '@/hooks/use-tasks';
import { usePriorityMutations, usePriorityHistory, getPriorityColor, getPriorityLabel } from '@/hooks/use-priority';
import { Card, CardHeader, CardContent, Button } from '@/components/ui';
import { TaskEditModal } from '@/components/tasks';
import type { Task, UpdateTaskInput } from '@/types';

/**
 * Priority score badge component - clickable to change priority
 */
function PriorityBadge({ 
  score, 
  isPinned,
  showMenu,
  onToggleMenu,
  onSetPriority,
}: { 
  score?: number; 
  isPinned?: boolean;
  showMenu?: boolean;
  onToggleMenu?: () => void;
  onSetPriority?: (level: 'high' | 'medium' | 'low') => void;
}) {
  const displayScore = score ?? 50;
  const colorClass = getPriorityColor(displayScore);
  const label = getPriorityLabel(displayScore);

  return (
    <div className="relative">
      <button
        onClick={onToggleMenu}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold transition-all duration-300 hover:ring-2 hover:ring-offset-1 hover:ring-stone-300 cursor-pointer ${colorClass}`}
        title="Click to change priority"
      >
        {isPinned && <Pin className="w-3.5 h-3.5 fill-current" />}
        <span className="tabular-nums text-base transition-all duration-300">{Math.round(displayScore)}</span>
        <span className="text-xs opacity-75 font-medium">({label})</span>
        <ChevronDown className="w-3 h-3 opacity-50" />
      </button>
      
      {showMenu && onSetPriority && (
        <div 
          className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-20 min-w-[140px]"
          onMouseLeave={onToggleMenu}
        >
          <button
            onClick={() => onSetPriority('high')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50"
          >
            <span className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="font-medium text-orange-600">High</span>
            <span className="text-stone-400 ml-auto">80</span>
          </button>
          <button
            onClick={() => onSetPriority('medium')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50"
          >
            <span className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="font-medium text-yellow-600">Medium</span>
            <span className="text-stone-400 ml-auto">50</span>
          </button>
          <button
            onClick={() => onSetPriority('low')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50"
          >
            <span className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="font-medium text-blue-600">Low</span>
            <span className="text-stone-400 ml-auto">20</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Task review card with priority actions
 */
function ReviewCard({ 
  task, 
  onTouch, 
  onBump, 
  onDemote, 
  onPin, 
  onUnpin,
  onComplete,
  onNotMine,
  onNotATask,
  onShowHistory,
  onEdit,
  onSetPriority,
  isProcessing,
}: { 
  task: Task;
  onTouch: () => void;
  onBump: () => void;
  onDemote: () => void;
  onPin: () => void;
  onUnpin: () => void;
  onComplete: () => void;
  onNotMine: () => void;
  onNotATask: () => void;
  onShowHistory: () => void;
  onEdit: () => void;
  onSetPriority: (level: 'high' | 'medium' | 'low') => void;
  isProcessing: boolean;
}) {
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  
  // Local state for immediate visual feedback
  const [localPriorityScore, setLocalPriorityScore] = useState(task.priorityScore ?? 50);
  const [localIsPinned, setLocalIsPinned] = useState(task.isPinned ?? false);
  
  // Track the last local change time to prevent server data from overwriting recent local changes
  const lastLocalChangeRef = useRef<number>(0);
  
  // Sync local state when task props change from server
  // But only if we haven't made a local change in the last 5 seconds
  useEffect(() => {
    const timeSinceLastChange = Date.now() - lastLocalChangeRef.current;
    if (timeSinceLastChange > 5000) {
      setLocalPriorityScore(task.priorityScore ?? 50);
      setLocalIsPinned(task.isPinned ?? false);
    }
  }, [task.priorityScore, task.isPinned]);
  
  // Also sync when task ID changes (user switched to different task)
  useEffect(() => {
    setLocalPriorityScore(task.priorityScore ?? 50);
    setLocalIsPinned(task.isPinned ?? false);
    lastLocalChangeRef.current = 0; // Reset on task change
  }, [task.id]);
  
  const needsReview = task.needsReview ?? false;
  
  // Handlers that update local state immediately
  const handleBump = () => {
    lastLocalChangeRef.current = Date.now();
    setLocalPriorityScore(prev => Math.min(100, prev + 10));
    onBump();
  };
  
  const handleDemote = () => {
    lastLocalChangeRef.current = Date.now();
    setLocalPriorityScore(prev => Math.max(0, prev - 10));
    onDemote();
  };
  
  const handlePin = () => {
    lastLocalChangeRef.current = Date.now();
    setLocalIsPinned(true);
    onPin();
  };
  
  const handleUnpin = () => {
    lastLocalChangeRef.current = Date.now();
    setLocalIsPinned(false);
    onUnpin();
  };
  
  const handleSetPriority = (level: 'high' | 'medium' | 'low') => {
    lastLocalChangeRef.current = Date.now();
    const scores = { high: 80, medium: 50, low: 20 };
    setLocalPriorityScore(scores[level]);
    setShowPriorityMenu(false);
    onSetPriority(level);
  };

  return (
    <Card className={`transition-all duration-300 ${needsReview ? 'ring-2 ring-amber-300 bg-amber-50/30' : ''} ${localIsPinned ? 'border-l-4 border-l-amber-400 bg-amber-50/20' : ''}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Pinned indicator */}
          {localIsPinned && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium animate-pulse">
              <Pin className="w-3.5 h-3.5 fill-amber-400" />
              <span>📌 Pinned</span>
            </div>
          )}

          {/* Header with title and priority */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div 
                className="group cursor-pointer"
                onClick={onEdit}
                title="Click to edit"
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-stone-900 group-hover:text-primary-700">{task.title}</h3>
                  <Pencil className="w-3.5 h-3.5 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {(task.description || task.notes) && (
                  <p className="text-sm text-stone-500 mt-1 line-clamp-2">{task.description || task.notes}</p>
                )}
              </div>
              {task.topicPath && (
                <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                  <span className="text-stone-300">📁</span>
                  {task.topicPath}
                </p>
              )}
            </div>
            <PriorityBadge 
              score={localPriorityScore} 
              isPinned={localIsPinned}
              showMenu={showPriorityMenu}
              onToggleMenu={() => setShowPriorityMenu(!showPriorityMenu)}
              onSetPriority={handleSetPriority}
            />
          </div>

          {/* Review reason if present */}
          {needsReview && task.reviewReason && (
            <div className="flex items-center gap-2 p-2 bg-amber-100 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{task.reviewReason}</span>
            </div>
          )}

          {/* Due date if present */}
          {task.dueAt && (
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <span className="text-stone-400">📅</span>
              <span>Due: {new Date(task.dueAt).toLocaleDateString()}</span>
            </div>
          )}

          {/* Primary action buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-stone-100">
            {/* Touch - reset decay clock */}
            <Button
              variant="outline"
              size="sm"
              onClick={onTouch}
              disabled={isProcessing}
              className="gap-1.5"
              title="Reset priority decay timer"
            >
              <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
              Touch
            </Button>

            {/* Bump up */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleBump}
              disabled={isProcessing}
              className="gap-1.5 text-green-600 hover:bg-green-50"
              title="Increase priority by 10"
            >
              <ChevronUp className="w-4 h-4" />
              Bump
            </Button>

            {/* Demote */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDemote}
              disabled={isProcessing}
              className="gap-1.5 text-stone-500 hover:bg-stone-50"
              title="Decrease priority by 10"
            >
              <ChevronDown className="w-4 h-4" />
              Demote
            </Button>

            {/* Pin/Unpin */}
            {localIsPinned ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnpin}
                disabled={isProcessing}
                className="gap-1.5 text-amber-600"
                title="Remove pin - allow priority to decay"
              >
                <PinOff className="w-4 h-4" />
                Unpin
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePin}
                disabled={isProcessing}
                className="gap-1.5"
                title="Pin to resist priority decay for 7 days"
              >
                <Pin className="w-4 h-4" />
                Pin
              </Button>
            )}

            {/* Complete */}
            <Button
              variant="outline"
              size="sm"
              onClick={onComplete}
              disabled={isProcessing}
              className="gap-1.5 text-green-600 hover:bg-green-50"
              title="Mark as completed"
            >
              <Check className="w-4 h-4" />
              Done
            </Button>

            {/* Not a Task */}
            <Button
              variant="outline"
              size="sm"
              onClick={onNotATask}
              disabled={isProcessing}
              className="gap-1.5 text-blue-600 hover:bg-blue-50"
              title="This is relevant info but not an actionable task"
            >
              <FileText className="w-4 h-4" />
              Not a Task
            </Button>

            {/* Remove */}
            <Button
              variant="outline"
              size="sm"
              onClick={onNotMine}
              disabled={isProcessing}
              className="gap-1.5 text-red-600 hover:bg-red-50"
              title="Remove task (won't return after sync)"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </Button>

            {/* More actions dropdown */}
            <div className="relative ml-auto">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMoreActions(!showMoreActions)}
                className="gap-1.5"
                title="More actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
              
              {showMoreActions && (
                <div 
                  className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-10 min-w-[160px]"
                  onMouseLeave={() => setShowMoreActions(false)}
                >
                  {/* Edit */}
                  <button
                    onClick={() => { onEdit(); setShowMoreActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Task
                  </button>
                  {/* History */}
                  <button
                    onClick={() => { onShowHistory(); setShowMoreActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  >
                    <History className="w-4 h-4" />
                    View History
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Priority history modal
 */
function HistoryModal({ 
  taskId, 
  taskTitle,
  onClose 
}: { 
  taskId: string;
  taskTitle: string;
  onClose: () => void;
}) {
  const { data, isLoading } = usePriorityHistory(taskId, 20);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-stone-900">Priority History</h2>
              <p className="text-sm text-stone-500 truncate">{taskTitle}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="p-8 text-center text-stone-500">Loading...</div>
          ) : !data?.events.length ? (
            <div className="p-8 text-center text-stone-500">No history yet</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {data.events.map((event) => (
                <div key={event.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2">
                      {event.score_delta > 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : event.score_delta < 0 ? (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      ) : (
                        <RefreshCw className="w-4 h-4 text-blue-500" />
                      )}
                      <span className="text-sm font-medium text-stone-900">
                        {event.event_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <span className="text-xs text-stone-400">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-stone-500">{Math.round(event.previous_score)}</span>
                    <span className="text-stone-400">→</span>
                    <span className="font-medium text-stone-900">{Math.round(event.new_score)}</span>
                    <span className={`text-xs ${event.score_delta > 0 ? 'text-green-600' : event.score_delta < 0 ? 'text-red-600' : 'text-stone-400'}`}>
                      ({event.score_delta > 0 ? '+' : ''}{event.score_delta.toFixed(1)})
                    </span>
                  </div>
                  {event.reason && (
                    <p className="mt-1 text-xs text-stone-500">{event.reason}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Main Review Page
 */
export function ReviewPage() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string>('');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [processingTasks, setProcessingTasks] = useState<Set<string>>(new Set());

  // Fetch tasks sorted by priority score (highest first)
  // Note: We fetch only verified tasks (not inbox), without status filter
  // to use the priorityScore index, then filter client-side for incomplete tasks
  const { data: tasksData, isLoading, refetch } = useTasks(
    { verificationStatus: 'verified' },  // Only verified tasks (not inbox)
    { sortBy: 'priorityScore', sortOrder: 'desc', limit: 100 }
  );

  const { touch, pin, unpin } = usePriorityMutations();
  const { dismissTask, convertToReference, completeTask, updateTask } = useTaskMutations();

  // Celebration effect for completing important tasks
  const triggerCelebration = useCallback(() => {
    // Fire confetti from both sides
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999,
    };

    function fire(particleRatio: number, opts: confetti.Options) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // Burst from left
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      origin: { x: 0.2, y: 0.7 },
    });

    // Burst from right
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      origin: { x: 0.8, y: 0.7 },
    });

    // Center burst with more spread
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });

    // Some slower, larger confetti
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });

    // And some stars
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
      shapes: ['star'],
      colors: ['#FFD700', '#FFA500', '#FF6347'],
    });
  }, []);

  // Filter client-side for incomplete tasks (pending, in_progress)
  const incompleteTasks = (tasksData?.tasks ?? []).filter(
    t => t.status === 'pending' || t.status === 'in_progress'
  );
  
  // Sort by priority score descending (highest first)
  const sortByPriority = (a: Task, b: Task) => 
    (b.priorityScore ?? 50) - (a.priorityScore ?? 50);
  
  // Separate tasks needing review, both sorted by priority
  const needsReviewTasks = incompleteTasks
    .filter(t => t.needsReview)
    .sort(sortByPriority);
  const normalTasks = incompleteTasks
    .filter(t => !t.needsReview)
    .sort(sortByPriority);

  const withProcessing = async (taskId: string, fn: () => Promise<unknown>) => {
    setProcessingTasks(prev => new Set([...prev, taskId]));
    try {
      await fn();
    } finally {
      setProcessingTasks(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  const handleTouch = (taskId: string) => 
    withProcessing(taskId, () => touch.mutateAsync({ taskId }));

  const handleBump = (taskId: string) => 
    withProcessing(taskId, () => touch.mutateAsync({ taskId, bumpAmount: 10 }));

  const handleDemote = (taskId: string) => 
    withProcessing(taskId, () => touch.mutateAsync({ taskId, bumpAmount: -10 }));

  const handlePin = (taskId: string) => 
    withProcessing(taskId, () => pin.mutateAsync({ taskId, reason: 'Manually pinned', days: 7 }));

  const handleUnpin = (taskId: string) => 
    withProcessing(taskId, () => unpin.mutateAsync(taskId));

  const handleComplete = (taskId: string, isPinned?: boolean, priorityScore?: number) => 
    withProcessing(taskId, async () => {
      await completeTask.mutateAsync(taskId);
      // Celebrate completing pinned or high priority (>= 70) tasks!
      if (isPinned || (priorityScore && priorityScore >= 70)) {
        triggerCelebration();
      }
    });

  const handleNotMine = (taskId: string) => 
    withProcessing(taskId, () => dismissTask.mutateAsync(taskId));

  const handleNotATask = (taskId: string) => 
    withProcessing(taskId, () => convertToReference.mutateAsync(taskId));

  const handleSaveTask = (taskId: string, updates: UpdateTaskInput) => {
    updateTask.mutate({ taskId, input: updates }, {
      onSuccess: () => {
        setEditingTask(null);
      },
    });
  };

  const handleSetPriority = (taskId: string, currentScore: number, level: 'high' | 'medium' | 'low') => {
    const targetScores = { high: 80, medium: 50, low: 20 };
    const targetScore = targetScores[level];
    const bumpAmount = targetScore - currentScore;
    // Use touch with bump to set the priority
    return withProcessing(taskId, () => touch.mutateAsync({ taskId, bumpAmount }));
  };

  const handleShowHistory = (taskId: string, title: string) => {
    setSelectedTaskId(taskId);
    setSelectedTaskTitle(title);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary-100 rounded-xl">
            <Sparkles className="w-6 h-6 text-primary-700" />
          </div>
          <h1 className="text-2xl font-display font-bold text-stone-900">Priority Review</h1>
        </div>
        <p className="text-stone-600">
          Review your tasks and ensure you're focusing on what matters most.
        </p>
      </div>

      {/* Refresh button */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Needs Review Section */}
      {needsReviewTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-stone-900">
              Needs Review ({needsReviewTasks.length})
            </h2>
          </div>
          <p className="text-sm text-stone-500 mb-4">
            These tasks need your attention. Consider whether they're still relevant.
          </p>
          <div className="space-y-4">
            {needsReviewTasks.map(task => (
              <ReviewCard
                key={task.id}
                task={task}
                onTouch={() => handleTouch(task.id)}
                onBump={() => handleBump(task.id)}
                onDemote={() => handleDemote(task.id)}
                onPin={() => handlePin(task.id)}
                onUnpin={() => handleUnpin(task.id)}
                onComplete={() => handleComplete(task.id, task.isPinned, task.priorityScore ?? undefined)}
                onNotMine={() => handleNotMine(task.id)}
                onNotATask={() => handleNotATask(task.id)}
                onShowHistory={() => handleShowHistory(task.id, task.title)}
                onEdit={() => handleEditTask(task)}
                onSetPriority={(level) => handleSetPriority(task.id, task.priorityScore ?? 50, level)}
                isProcessing={processingTasks.has(task.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Tasks by Priority */}
      <div>
        <h2 className="text-lg font-semibold text-stone-900 mb-4">
          Tasks by Priority
        </h2>
        
        {isLoading ? (
          <div className="p-8 text-center text-stone-500">Loading tasks...</div>
        ) : normalTasks.length === 0 && needsReviewTasks.length === 0 ? (
          <Card className="p-8 text-center">
            <Mountain className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No active tasks</p>
            <p className="text-sm text-stone-400 mt-1">
              Create tasks to start tracking priorities
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {normalTasks.map(task => (
              <ReviewCard
                key={task.id}
                task={task}
                onTouch={() => handleTouch(task.id)}
                onBump={() => handleBump(task.id)}
                onDemote={() => handleDemote(task.id)}
                onPin={() => handlePin(task.id)}
                onUnpin={() => handleUnpin(task.id)}
                onComplete={() => handleComplete(task.id, task.isPinned, task.priorityScore ?? undefined)}
                onNotMine={() => handleNotMine(task.id)}
                onNotATask={() => handleNotATask(task.id)}
                onShowHistory={() => handleShowHistory(task.id, task.title)}
                onEdit={() => handleEditTask(task)}
                onSetPriority={(level) => handleSetPriority(task.id, task.priorityScore ?? 50, level)}
                isProcessing={processingTasks.has(task.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* History Modal */}
      {selectedTaskId && (
        <HistoryModal
          taskId={selectedTaskId}
          taskTitle={selectedTaskTitle}
          onClose={() => setSelectedTaskId(null)}
        />
      )}

      {/* Edit Task Modal - Same unified modal used everywhere */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          onComplete={(taskId) => {
            handleComplete(taskId, editingTask.isPinned, editingTask.priorityScore ?? undefined);
            setEditingTask(null);
          }}
          onDismiss={(taskId) => {
            handleNotMine(taskId);
            setEditingTask(null);
          }}
          onConvertToReference={(taskId) => {
            handleNotATask(taskId);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
}
